import React, { useEffect } from 'react';

import { Queries } from '@/queries/Queries.ts';

type NoteState =
  | {
      state: 'noNote';
    }
  | {
      state: 'viewing';
      note: string;
    }
  | {
      state: 'editing';
      initialNote?: string;
      currentNote?: string;
    };

type NoteAction =
  | {
      type: 'startEditing' | 'cancel' | 'save';
    }
  | {
      type: 'refresh';
      note?: string;
    }
  | {
      type: 'editNote';
      note: string;
    };

const noteStateReducer = (state: NoteState, action: NoteAction): NoteState => {
  switch (action.type) {
    case 'refresh': {
      if (state.state === 'viewing' || state.state === 'noNote') {
        return action.note
          ? { state: 'viewing', note: action.note }
          : { state: 'noNote' };
      }
      return state;
    }
    case 'startEditing': {
      const note = state.state === 'viewing' ? state.note : undefined;
      return {
        state: 'editing',
        initialNote: note,
        currentNote: note,
      };
    }
    case 'editNote': {
      if (state.state !== 'editing') {
        return state;
      }
      return {
        ...state,
        currentNote: action.note,
      };
    }
    case 'cancel': {
      if (state.state !== 'editing') {
        return state;
      }
      return state.initialNote
        ? { state: 'viewing', note: state.initialNote }
        : { state: 'noNote' };
    }
    case 'save': {
      if (state.state !== 'editing') {
        return state;
      }
      return state.currentNote
        ? {
            state: 'viewing',
            note: state.currentNote,
          }
        : { state: 'noNote' };
    }
  }
};

export type UsePatientNoteReturn = {
  content?: string;
  edit: () => void;
  cancel: () => void;
  save: () => void;
  editNote: (note: string) => void;
  editing: boolean;
  isNewNote: boolean;
};

export const usePatientNote = (
  patientId: string,
  onSuccess?: () => void,
  onError?: () => void,
): UsePatientNoteReturn => {
  const note = Queries.notes.useNote({
    patientId,
  });
  const writeNote = Queries.notes.useWriteNote();
  const [noteState, dispatch] = React.useReducer(
    noteStateReducer,
    noteStateReducer(
      { state: 'noNote' },
      { type: 'refresh', note: note.content },
    ),
  );

  const edit = () => dispatch({ type: 'startEditing' });
  const cancel = () => dispatch({ type: 'cancel' });
  const refresh = (note?: string) => dispatch({ type: 'refresh', note });
  const editNote = (note: string) => dispatch({ type: 'editNote', note });
  const save = () => {
    if (noteState.state !== 'editing') {
      return;
    }
    const note = noteState.currentNote;
    dispatch({ type: 'save' });
    writeNote.mutate(
      {
        patientId,
        note,
      },
      {
        onSuccess,
        onError,
      },
    );
  };

  useEffect(() => {
    refresh(note.content);
  }, [note.content]);

  const content =
    noteState.state === 'viewing'
      ? noteState.note
      : noteState.state === 'editing'
        ? noteState.currentNote
        : undefined;

  return {
    content,
    edit,
    cancel,
    save,
    editNote,
    editing: noteState.state === 'editing',
    isNewNote: noteState.state === 'noNote',
  };
};
